import {
  getFromLocalStorage,
  setToLocalStorage,
  LOCAL_STORAGE_ITEM,
} from '@utils/localStorageHelper';

import { isCognitoUserAlreadyConfirmedError } from '@services/cognitoErrorNormalizer';

const CONFIRMATION_RESEND_COOLDOWN_MS = 60 * 1000;

const normalizeEmailKey = (email) => String(email || '').trim().toLowerCase();

const getLastResendMap = () => {
  try {
    return (
      getFromLocalStorage(LOCAL_STORAGE_ITEM.COGNITO_LAST_CONFIRMATION_RESEND) ||
      {}
    );
  } catch (_) {
    return {};
  }
};

export const canAutoResendEmailVerification = (email) => {
  const key = normalizeEmailKey(email);
  if (!key) return false;

  const map = getLastResendMap();
  const last = Number(map[key] || 0);

  if (!Number.isFinite(last) || last <= 0) return true;
  return Date.now() - last >= CONFIRMATION_RESEND_COOLDOWN_MS;
};

export const markAutoResentEmailVerification = (email) => {
  const key = normalizeEmailKey(email);
  if (!key) return;

  const map = getLastResendMap();
  map[key] = Date.now();
  setToLocalStorage(LOCAL_STORAGE_ITEM.COGNITO_LAST_CONFIRMATION_RESEND, map);
};

export const normalizeCognitoVerificationErrorMessage = (err, options = {}) => {
  const { t, fallback } = options;
  const translate = (key, defaultMessage) => (
    typeof t === 'function' ? t(key, defaultMessage) : defaultMessage
  );

  if (!err) {
    return fallback || translate('Errors.verificationFailedTryAgain', 'Verification failed. Please try again.');
  }
  if (typeof err === 'string') {
    return err || fallback || translate('Errors.verificationFailedTryAgain', 'Verification failed. Please try again.');
  }

  const code = err?.code || err?.name || err?.__type;
  const message = err?.message || '';

  if (isCognitoUserAlreadyConfirmedError(err)) {
    return translate(
      'Errors.verificationCodeAlreadyConfirmed',
      "We couldn't resend a confirmation code because this account is already confirmed. If you still can't sign in, please contact support.",
    );
  }

  if (code === 'CodeMismatchException') {
    return translate('Errors.invalidVerificationCode', 'Incorrect verification code. Please try again.');
  }

  if (code === 'ExpiredCodeException') {
    return translate('Errors.verificationCodeExpired', 'This verification code has expired. Please click Resend to get a new one.');
  }

  if (code === 'LimitExceededException' || code === 'TooManyRequestsException') {
    return translate('Errors.tooManyAttempts', 'Too many attempts. Please try again later.');
  }

  return message || fallback || translate('Errors.verificationFailedTryAgain', 'Verification failed. Please try again.');
};
